import { useEffect, useId, useRef, useState } from "react";
import type { JSX, ReactNode } from "react";
import { useOffline } from "../../offline/OfflineContext";
import { countUnsentEntriesOnDevice } from "../../offline/unsentEntries";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { LegalLinkList, useLegalLinks } from "../legal/LegalLinks";
import { useSessionActions } from "../session/SessionActions";
import { Alert } from "./Alert";
import { BrandHeader } from "./BrandHeader";
import { cx } from "./classNames";
import { useFocusOnMount } from "./focus";
import { PageHeader } from "./PageHeader";
import layoutStyles from "./PublicLayout.module.css";
import styles from "./ui.module.css";

/** Properties of {@link PublicLayout}. */
export interface PublicLayoutProps {
  /** Page title: the level-one heading and the document title. */
  readonly title: string;
  /** Content below the heading. */
  readonly children: ReactNode;
  /**
   * Look of a sign-in screen (F-44, ADR-0160): `hero` for the web sign-in on the navy ground, `signIn`
   * for the e-mail step, the tenant choice and the login failure on the light ground. Omitted, the
   * plain layout of the registration and the error pages stays as it is.
   */
  readonly look?: "hero" | "signIn" | undefined;
}

/**
 * Layout of the screens outside the application shell (UX-120): sign-in, login failure, tenant
 * selection, registration and route errors. It shows the product mark (ADR-0107), keeps a gutter
 * that includes the safe areas (P-10), puts the content into `main` below a page header that also
 * sets the document title (WCAG 2.4.2), and offers „Abmelden“ while a session exists, so nobody is
 * stuck on such a screen (H-3). The native apps show the links to the legal pages in the footer.
 *
 * @param props - Component properties.
 * @returns The page.
 */
export function PublicLayout(props: PublicLayoutProps): JSX.Element {
  const { status } = useAuth();
  const legalLinks = useLegalLinks();
  const hero = props.look === "hero";
  const signInLook = props.look === "signIn";

  const layout = (
    <div
      className={cx(
        layoutStyles["layout"],
        hero ? layoutStyles["hero"] : undefined,
        signInLook ? layoutStyles["signIn"] : undefined,
      )}
    >
      <div className={layoutStyles["content"]}>
        <header
          className={cx(layoutStyles["header"], hero ? layoutStyles["heroHeader"] : undefined)}
        >
          {hero ? <BrandHeader variant="hero" /> : <BrandHeader />}
          {status === "signedIn" ? <SignOutAction /> : null}
        </header>
        <main className={cx(styles["page"], hero ? layoutStyles["heroMain"] : undefined)}>
          <PageHeader title={props.title} />
          {props.children}
        </main>
        {legalLinks.length === 0 ? null : (
          <footer
            className={cx(layoutStyles["footer"], hero ? layoutStyles["heroFooter"] : undefined)}
          >
            {/* Privacy policy and imprint before the sign-in (Apple 5.1.1(i), SAAS-IMPRINT). */}
            <LegalLinkList />
          </footer>
        )}
      </div>
    </div>
  );

  return layout;
}

/**
 * „Abmelden“ of the public screens. Like the sign-out on „Mehr“ it asks first while entries are
 * not yet sent, because signing out deletes them from the device (spec 11.4); it counts those of
 * every person and tenant on the device, as the sign-out deletes them all (F-84).
 *
 * @returns The link-styled button or the open confirmation.
 */
function SignOutAction(): JSX.Element {
  const { t } = useI18n();
  const { signOut } = useSessionActions();
  const { store } = useOffline();
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef(false);

  useEffect(() => {
    if (pendingCount === null && restoreFocusRef.current) {
      restoreFocusRef.current = false;
      triggerRef.current?.focus();
    }
  }, [pendingCount]);

  // A failed sign-out leaves the person signed in; it says so instead of failing silently (F-57).
  const runSignOut = async (): Promise<void> => {
    setFailed(false);

    try {
      await signOut();
    } catch {
      setFailed(true);
    }
  };
  const requestSignOut = async (): Promise<void> => {
    // Read the store at the moment of the click: entries may have been added meanwhile. Unsent
    // project attributions are lost as well (F-57).
    const count = await countUnsentEntriesOnDevice(store);

    if (count > 0) {
      setPendingCount(count);
    } else {
      await runSignOut();
    }
  };

  if (pendingCount !== null) {
    const confirmation = (
      <PendingSignOutConfirmation
        count={pendingCount}
        onCancel={() => {
          restoreFocusRef.current = true;
          setPendingCount(null);
        }}
        onConfirm={() => {
          setPendingCount(null);
          void runSignOut();
        }}
      />
    );
    return confirmation;
  }

  const trigger = (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={cx(styles["linkButton"], layoutStyles["signOut"])}
        onClick={() => {
          void requestSignOut();
        }}
      >
        {t("auth.logout")}
      </button>
      {failed ? <Alert tone="danger">{t("auth.logoutFailed")}</Alert> : null}
    </>
  );

  return trigger;
}

/** Properties of {@link PendingSignOutConfirmation}. */
interface PendingSignOutConfirmationProps {
  /** Number of unsent entries. */
  readonly count: number;
  /** Keeps the session. */
  readonly onCancel: () => void;
  /** Signs out and deletes the entries. */
  readonly onConfirm: () => void;
}

/**
 * Confirmation of a sign-out that deletes unsent entries (S-5, WCAG 3.3.4): a group named by the
 * consequence that receives the focus; Escape and „Abbrechen“ keep the session.
 *
 * @param props - Component properties.
 * @returns The confirmation.
 */
function PendingSignOutConfirmation(props: PendingSignOutConfirmationProps): JSX.Element {
  const { t } = useI18n();
  const panelRef = useFocusOnMount<HTMLDivElement>();
  const messageId = useId();
  const confirmation = (
    <div
      ref={panelRef}
      role="group"
      aria-labelledby={messageId}
      tabIndex={-1}
      className={cx(
        styles["alert"],
        styles["alertDanger"],
        styles["confirmAction"],
        layoutStyles["confirmation"],
      )}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          props.onCancel();
        }
      }}
    >
      <p id={messageId}>{t("auth.logoutPendingWarning", { count: props.count })}</p>
      <div className={styles["buttonGrid"]}>
        <button type="button" className={styles["button"]} onClick={props.onCancel}>
          {t("common.cancel")}
        </button>
        <button
          type="button"
          className={cx(styles["button"], styles["danger"])}
          onClick={props.onConfirm}
        >
          {t("auth.logoutConfirm")}
        </button>
      </div>
    </div>
  );

  return confirmation;
}
